import {
  Box,
  OutlinedInput,
  Typography,
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

import CustomButton from "../components/CustomButton";
import { selectUser } from "../store/slice/user";
import { sendDeleteConfirmation, verifyDeleteVerification } from "../services/profile";
import { CustomStyle } from "./customStyle";

const DeleteAccount = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const user = useSelector(selectUser);
  const language = user?.language;
  const isArabic = language === "ar";

  const query = useMemo(() => new URLSearchParams(location.search), [location]);
  const email = query.get("email");

  const [loading, setLoading] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState("");

  /* ─────────────────────────────
     Send delete confirmation mail
  ───────────────────────────── */
  useEffect(() => {
    if (!email) {
      toast.error("Invalid delete link");
      navigate("/");
      return;
    }

    setLoading(true);
    sendDeleteConfirmation({ email })
      .then(() => {
        setOtpSent(true);
        toast.success(t("delete_account.otp_sent"));
      })
      .catch((err) => {
        toast.error(err?.response?.data?.data?.message || "Something went wrong");
        navigate("/");
      })
      .finally(() => setLoading(false));
  }, [email, navigate, t]);

  /* ─────────────────────────────
     OTP handlers
  ───────────────────────────── */
  const handleOtpChange = (e) => {
    const value = e.target.value.replace(/\D/g, "");
    if (value.length <= 6) {
      setOtp(value);
    }
  };

  const handleVerify = async () => {
    if (otp.length !== 6) return;

    setLoading(true);
    verifyDeleteVerification({ email, otp })
      .then(() => {
        toast.success(t("delete_account.success"));
        navigate("/");
      })
      .catch((err) => {
        toast.error(err?.response?.data?.data?.message || "Invalid code");
      })
      .finally(() => setLoading(false));
  };

  /* ─────────────────────────────
     UI
  ───────────────────────────── */
  return (
    <Box sx={CustomStyle.pageContainer}>
      <Typography sx={CustomStyle.heading(isArabic)}>
        {t("delete_account.title")}
      </Typography>

      <Typography sx={CustomStyle.subHeading(isArabic)}>
        {t("delete_account.subtitle")}
      </Typography>

      {otpSent && (
        <Box sx={CustomStyle.otpContainer}>
          <Typography sx={CustomStyle.label(isArabic)}>
            {t("delete_account.enter_code")}
          </Typography>

          <OutlinedInput
            sx={CustomStyle.otpInput}
            value={otp}
            onChange={handleOtpChange}
            placeholder="------"
            inputProps={{
              maxLength: 6,
              inputMode: "numeric",
              style: {
                textAlign: "center",
                letterSpacing: "12px",
                fontSize: "20px",
              },
            }}
          />
        </Box>
      )}

      <CustomButton
        buttonText={t("delete_account.confirm")}
        color="secondary"
        loading={loading}
        disabled={otp.length !== 6}
        sx={CustomStyle.button(isArabic)}
        onClick={handleVerify}
      />
    </Box>
  );
};

export default DeleteAccount;
